import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  RefreshCw, Zap, CheckCircle2, AlertTriangle, XCircle, Gamepad2,
  Clock, Wallet, Layers, Activity, Loader2, BarChart3, ArrowUpCircle,
  ArrowDownCircle, TrendingDown, Users, Calendar,
} from 'lucide-react';

const fmtUSD = (v) =>
  v === null || v === undefined
    ? '—'
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function ProviderBalancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingAll, setRefreshingAll] = useState(false);
  // Per-provider action state: { [code]: { checking: bool, testing: bool, testResult: {...} } }
  const [rowState, setRowState] = useState({});

  // Flow Analytics state — player load/redeem by provider over a period
  const [period, setPeriod] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const fetchCached = async () => {
    try {
      const res = await api.get('/api/admin/provider-balance/');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load provider balances');
    } finally {
      setLoading(false);
    }
  };

  const refreshAllLive = async () => {
    setRefreshingAll(true);
    try {
      const res = await api.get('/api/admin/provider-balance/?live=true');
      setData(res.data);
      toast.success('Live sweep complete — balances refreshed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshingAll(false);
    }
  };

  const checkOne = async (code) => {
    setRowState((s) => ({ ...s, [code]: { ...(s[code] || {}), checking: true } }));
    try {
      const res = await api.post(`/api/admin/provider-balance/${code}/check`);
      // Patch the local row with the fresh data
      setData((prev) => {
        if (!prev) return prev;
        const items = prev.items.map((r) =>
          r.provider_code === code
            ? {
                ...r,
                balance: res.data.balance,
                ok: res.data.ok,
                error: res.data.error,
                last_checked_at: res.data.checked_at,
                is_low: !!(res.data.ok && res.data.balance < 500),
              }
            : r
        );
        const healthy = items.filter((r) => r.ok).length;
        const total = items.reduce((a, r) => a + (r.ok && r.balance ? r.balance : 0), 0);
        return {
          ...prev,
          items,
          healthy_providers: healthy,
          total_balance_usd: total,
          low_balance_count: items.filter((r) => r.ok && r.balance < 500).length,
        };
      });
      if (res.data.ok) {
        toast.success(`${code} balance: ${fmtUSD(res.data.balance)}`);
      } else {
        toast.error(`${code} check failed: ${res.data.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Check failed');
    } finally {
      setRowState((s) => ({ ...s, [code]: { ...(s[code] || {}), checking: false } }));
    }
  };

  const testOne = async (code) => {
    setRowState((s) => ({
      ...s,
      [code]: { ...(s[code] || {}), testing: true, testResult: null },
    }));
    try {
      const res = await api.post(`/api/admin/provider-balance/${code}/test-connection`);
      setRowState((s) => ({
        ...s,
        [code]: { ...(s[code] || {}), testing: false, testResult: res.data },
      }));
      if (res.data.ok) {
        toast.success(`${code} connection OK (${res.data.duration_ms} ms)`);
      } else {
        toast.error(
          `${code} connection failed: ${res.data.error || 'handler not registered'}`
        );
      }
    } catch (e) {
      setRowState((s) => ({ ...s, [code]: { ...(s[code] || {}), testing: false } }));
      toast.error(e?.response?.data?.detail || 'Test failed');
    }
  };

  useEffect(() => {
    fetchCached();
  }, []);

  // Fetch flow analytics whenever the period (or custom range) changes.
  // Silent auto-refresh every 45s so operators see live values without
  // screen blinking.
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const params = { period };
      if (period === 'custom') {
        if (!customFrom || !customTo) {
          setAnalyticsLoading(false);
          return;
        }
        params.date_from = new Date(customFrom).toISOString();
        params.date_to = new Date(customTo).toISOString();
      }
      const res = await api.get('/api/admin/provider-balance/analytics', { params });
      setAnalytics(res.data);
    } catch (e) {
      // swallow; banner handles empty state
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    const t = setInterval(fetchAnalytics, 45000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customFrom, customTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="provider-balance-loading">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;
  const items = data.items || [];
  const lowCount = data.low_balance_count ?? 0;

  return (
    <div className="space-y-6" data-testid="provider-balance-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wallet className="w-6 h-6 text-[#D4AF37]" /> Provider Balance
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Live upstream game-provider pool balances. Auto-refreshes every hour; master
            admins get low-balance alerts when any provider drops below&nbsp;
            <span className="text-amber-400 font-semibold">$500</span>.
          </p>
        </div>
        <button
          onClick={refreshAllLive}
          disabled={refreshingAll}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#D4AF37] text-black font-semibold hover:bg-[#F5D76E] disabled:opacity-50 transition"
          data-testid="refresh-all-balances-btn"
        >
          {refreshingAll ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          {refreshingAll ? 'Sweeping…' : 'Refresh All (live)'}
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="provider-balance-summary">
        <SummaryTile
          icon={Wallet}
          label="Combined Pool"
          value={fmtUSD(data.total_balance_usd)}
          color="#22c55e"
          testid="summary-total"
        />
        <SummaryTile
          icon={CheckCircle2}
          label="Healthy / Total"
          value={`${data.healthy_providers} / ${data.total_providers}`}
          color="#3b82f6"
          testid="summary-healthy"
        />
        <SummaryTile
          icon={AlertTriangle}
          label={`Low (<$500)`}
          value={lowCount}
          color={lowCount ? '#f59e0b' : '#6b7280'}
          testid="summary-low"
        />
        <SummaryTile
          icon={Clock}
          label="Auto-refresh"
          value="every hour"
          color="#8b5cf6"
          testid="summary-auto"
          small
        />
      </div>

      {/* Provider cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="provider-cards-grid">
        {items.map((p) => (
          <ProviderCard
            key={p.provider_code}
            row={p}
            state={rowState[p.provider_code] || {}}
            onCheck={() => checkOne(p.provider_code)}
            onTest={() => testOne(p.provider_code)}
          />
        ))}
      </div>

      {items.length === 0 && (
        <div className="rounded-2xl p-8 bg-[#131822] border border-dashed border-white/10 text-center text-gray-400">
          <Layers className="w-12 h-12 mx-auto mb-3 text-gray-600" />
          No game providers configured yet.
        </div>
      )}

      {/* ─── Flow Analytics ─── Player load/redeem by provider over a date range */}
      <FlowAnalytics
        period={period}
        setPeriod={setPeriod}
        customFrom={customFrom}
        setCustomFrom={setCustomFrom}
        customTo={customTo}
        setCustomTo={setCustomTo}
        analytics={analytics}
        loading={analyticsLoading}
      />
    </div>
  );
}

function FlowAnalytics({ period, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo, analytics, loading }) {
  const periods = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'all', label: 'All Time' },
    { id: 'custom', label: 'Custom' },
  ];
  const combined = analytics?.combined;
  const rows = analytics?.per_provider || [];
  const byDay = analytics?.by_day || [];

  // Simple sparkline max for the daily bars
  const dayMax = Math.max(1, ...byDay.map((d) => Math.max(d.loaded_sc || 0, d.redeemed_sc || 0)));

  return (
    <div className="rounded-2xl bg-[#131822] border border-white/10 p-5 space-y-5" data-testid="provider-flow-analytics">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#D4AF37]" /> Flow Analytics
          </h3>
          <p className="text-[11px] text-gray-500 mt-1">
            Player load / redeem volume per provider. Live (45s refresh). All $ values reflect the wallet side; provider credits and balance redeemed also tracked.
          </p>
        </div>
        {loading ? <Loader2 className="w-4 h-4 text-gray-500 animate-spin" /> : null}
      </div>

      {/* Period pills */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="flow-period-pills">
        {periods.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              period === p.id
                ? 'bg-[#D4AF37] text-black'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid={`flow-period-${p.id}`}
          >
            {p.label}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2 text-xs ml-1">
            <Calendar className="w-3.5 h-3.5 text-gray-500" />
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-white" data-testid="flow-custom-from" />
            <span className="text-gray-500">→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-white" data-testid="flow-custom-to" />
          </div>
        )}
      </div>

      {/* Combined KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="flow-combined-kpis">
        <FlowKPI icon={<ArrowUpCircle className="w-4 h-4 text-emerald-400" />} label="Loaded" value={fmtUSD(combined?.loaded_sc ?? 0)} hint={`${combined?.load_count ?? 0} loads`} testid="flow-kpi-loaded" />
        <FlowKPI icon={<ArrowDownCircle className="w-4 h-4 text-[#D4AF37]" />} label="Redeemed" value={fmtUSD(combined?.redeemed_sc ?? 0)} hint={`${combined?.redeem_count ?? 0} redeems`} testid="flow-kpi-redeemed" />
        <FlowKPI icon={<TrendingDown className="w-4 h-4 text-fuchsia-300" />} label="Net (Redeem − Load)" value={fmtUSD(combined?.net_sc ?? 0)} hint={(combined?.net_sc ?? 0) < 0 ? 'players down' : 'players up'} testid="flow-kpi-net" />
        <FlowKPI icon={<Users className="w-4 h-4 text-sky-400" />} label="Unique Players" value={combined?.unique_players ?? 0} hint={`${combined?.unique_players_loaded ?? 0} loaders · ${combined?.unique_players_redeemed ?? 0} redeemers`} testid="flow-kpi-users" />
        <FlowKPI icon={<Activity className="w-4 h-4 text-indigo-300" />} label="Provider Credits Loaded" value={(combined?.loaded_provider_credits ?? 0).toLocaleString()} hint="raw upstream credits" testid="flow-kpi-provider-load" />
        <FlowKPI icon={<XCircle className="w-4 h-4 text-amber-400" />} label="Voided (Cap)" value={fmtUSD(combined?.voided_sc ?? 0)} hint="redeem cap clipping" testid="flow-kpi-voided" />
      </div>

      {/* Daily mini chart */}
      {byDay.length > 1 && (
        <div data-testid="flow-daily-chart">
          <div className="text-[10px] uppercase text-gray-500 mb-2">Daily Flow</div>
          <div className="flex items-end gap-1 h-24 bg-[#0B0E14] border border-white/5 rounded-xl p-3 overflow-x-auto">
            {byDay.map((d) => (
              <div key={d.date} className="flex flex-col items-center gap-1 min-w-[28px]">
                <div className="flex items-end gap-0.5 h-16">
                  <div title={`Loaded: ${fmtUSD(d.loaded_sc)}`} className="w-2.5 rounded-sm bg-emerald-400/70" style={{ height: `${Math.max(2, (d.loaded_sc / dayMax) * 64)}px` }} />
                  <div title={`Redeemed: ${fmtUSD(d.redeemed_sc)}`} className="w-2.5 rounded-sm bg-[#D4AF37]/80" style={{ height: `${Math.max(2, (d.redeemed_sc / dayMax) * 64)}px` }} />
                </div>
                <div className="text-[9px] text-gray-600">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400/70"></span>Loaded</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#D4AF37]/80"></span>Redeemed</span>
          </div>
        </div>
      )}

      {/* Per-provider rows */}
      <div>
        <div className="text-[10px] uppercase text-gray-500 mb-2">By Provider</div>
        <div className="rounded-xl border border-white/5 overflow-hidden" data-testid="flow-provider-table">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-gray-500 px-3 py-2 border-b border-white/5">
            <div className="col-span-3">Provider</div>
            <div className="col-span-2 text-right">Loaded $</div>
            <div className="col-span-2 text-right">Redeemed $</div>
            <div className="col-span-2 text-right">Net $</div>
            <div className="col-span-1 text-right">Loads</div>
            <div className="col-span-1 text-right">Redeems</div>
            <div className="col-span-1 text-right">Players</div>
          </div>
          {rows.length === 0 ? (
            <div className="px-3 py-6 text-center text-gray-500 text-sm">No activity in this window.</div>
          ) : (
            rows.map((r) => {
              const isNegative = (r.net_sc || 0) < 0;
              return (
                <div key={r.provider_code} className="grid grid-cols-12 gap-2 items-center px-3 py-2.5 border-b border-white/5 text-sm hover:bg-white/5" data-testid={`flow-row-${r.provider_code}`}>
                  <div className="col-span-3 font-semibold text-white uppercase tracking-wide">{r.provider_code}</div>
                  <div className="col-span-2 text-right text-emerald-400 tabular-nums">{r.loaded_sc.toLocaleString()}</div>
                  <div className="col-span-2 text-right text-[#D4AF37] tabular-nums">{r.redeemed_sc.toLocaleString()}</div>
                  <div className={`col-span-2 text-right tabular-nums ${isNegative ? 'text-fuchsia-300' : 'text-emerald-300'}`}>{r.net_sc.toLocaleString()}</div>
                  <div className="col-span-1 text-right text-gray-400 tabular-nums">{r.load_count}</div>
                  <div className="col-span-1 text-right text-gray-400 tabular-nums">{r.redeem_count}</div>
                  <div className="col-span-1 text-right text-gray-400 tabular-nums">{Math.max(r.unique_players_loaded, r.unique_players_redeemed)}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function FlowKPI({ icon, label, value, hint, testid }) {
  return (
    <div className="bg-[#0B0E14] border border-white/5 rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] uppercase text-gray-500 flex items-center gap-1">{icon} {label}</div>
      <div className="text-base font-bold text-white mt-1 tabular-nums truncate">{value}</div>
      {hint ? <div className="text-[10px] text-gray-600 mt-0.5 truncate">{hint}</div> : null}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, color, testid, small }) {
  return (
    <div
      className="rounded-2xl p-4 bg-[#131822] border border-white/10 flex items-center gap-3"
      data-testid={testid}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}18` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
        <p
          className={`${small ? 'text-base' : 'text-2xl'} font-bold tabular-nums text-white truncate`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function ProviderCard({ row, state, onCheck, onTest }) {
  const { checking, testing, testResult } = state;
  const isLow = row.ok && typeof row.balance === 'number' && row.balance < 500;
  const isUnsupported = row.status === 'unsupported';
  const isFailed = !row.ok && !isUnsupported;

  const ringColor = isFailed
    ? 'border-red-500/30 bg-red-500/5'
    : isUnsupported
      ? 'border-blue-500/25 bg-blue-500/5'
    : isLow
      ? 'border-amber-500/30 bg-amber-500/5'
      : 'border-white/10 bg-[#131822]';

  const StatusIcon = isFailed ? XCircle : isUnsupported ? Activity : isLow ? AlertTriangle : CheckCircle2;
  const statusColor = isFailed ? '#ef4444' : isUnsupported ? '#60a5fa' : isLow ? '#f59e0b' : '#22c55e';

  return (
    <div
      className={`rounded-2xl p-4 border ${ringColor} transition`}
      data-testid={`provider-card-${row.provider_code}`}
    >
      <div className="flex items-start gap-3">
        {row.logo_url ? (
          <img
            src={row.logo_url}
            alt={row.provider_name}
            className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
            <Gamepad2 className="w-7 h-7 text-gray-500" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-base font-semibold text-white truncate">
              {row.provider_name}
            </h4>
            <StatusIcon className="w-4 h-4 flex-shrink-0" style={{ color: statusColor }} />
          </div>
          <p className="text-[11px] text-gray-500 font-mono mt-0.5">{row.provider_code}</p>

          <div className="mt-2">
            {row.ok ? (
              <p
                className={`text-3xl font-bold tabular-nums ${
                  isLow ? 'text-amber-400' : 'text-green-400'
                }`}
                data-testid={`balance-${row.provider_code}`}
              >
                {fmtUSD(row.balance)}
              </p>
            ) : isUnsupported ? (
              <p
                className="text-xs text-blue-300 leading-tight"
                data-testid={`unsupported-${row.provider_code}`}
              >
                {row.error || 'Pool balance check is not supported for this provider.'}
              </p>
            ) : (
              <p
                className="text-xs text-red-300 leading-tight"
                data-testid={`error-${row.provider_code}`}
              >
                {row.error || 'No balance data yet — click Check Balance.'}
              </p>
            )}
            <p className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {row.last_checked_at ? `Checked ${fmtTime(row.last_checked_at)}` : 'Never checked'}
            </p>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={onCheck}
          disabled={checking || isUnsupported}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-semibold hover:bg-[#D4AF37]/20 disabled:opacity-40 transition"
          data-testid={`check-balance-btn-${row.provider_code}`}
        >
          {checking ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Wallet className="w-3.5 h-3.5" />
          )}
          {isUnsupported ? 'Balance N/A' : checking ? 'Checking…' : 'Check Balance'}
        </button>
        <button
          onClick={onTest}
          disabled={testing}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-semibold hover:bg-blue-500/20 disabled:opacity-40 transition"
          data-testid={`test-connection-btn-${row.provider_code}`}
        >
          {testing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Activity className="w-3.5 h-3.5" />
          )}
          {testing ? 'Testing…' : 'Test Connection'}
        </button>
      </div>

      {/* Test result inline */}
      {testResult && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2 text-[11px] ${
            testResult.ok
              ? 'bg-green-500/5 border-green-500/20 text-green-300'
              : 'bg-red-500/5 border-red-500/20 text-red-300'
          }`}
          data-testid={`test-result-${row.provider_code}`}
        >
          {testResult.ok ? (
            <span className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" />
              Connection OK · handler registered · {testResult.duration_ms} ms
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5" />
              {testResult.error ||
                (testResult.handler_registered === false
                  ? 'Handler not registered on upstream'
                  : 'Upstream not reachable')}
            </span>
          )}
        </div>
      )}

      {/* Low-balance warning footer */}
      {isLow && (
        <div className="mt-2 text-[11px] text-amber-300 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          Below $500 threshold — top up required.
        </div>
      )}
    </div>
  );
}
